import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { atomFamily } from 'jotai-family';
import { selectAtom } from 'jotai/utils';
import { memo, useEffect, useRef, useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { ConditionalOrderTypeEnum, Order, ordersBunja } from '@flipster/r230522/original/order';
import { OpenPosition, openPositionsBunja } from '@flipster/r230522/original/position';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import Num, { numberFormatter } from '@flipster/utils/num';
import segment, { ChartEvent } from '@flipster/utils/segment';

import { IPositionLineAdapter } from '../../../lib/charting_library';
import { ChartStyles } from '../../misc/constants';
import { useTradingViewDataStorage } from '../../misc/data-manager';
import { symbolNameToInfo } from '../../misc/helper';
import TradingView, { TvEventHandler } from '../../trading-view';
import { useTvHeaderContext } from '../index';
import { buildTpSlLabel, getTpSlExecutionLabel, type TpSlLabelVariant } from './tp-sl-label';

const tpSlBunja = bunja(() => {
  const { openPositionsAtom } = bunja.use(openPositionsBunja);
  const { v2OrdersAtom } = bunja.use(ordersBunja);

  const symbolPositionAtomFamily = atomFamily((symbol: string) =>
    selectAtom(
      openPositionsAtom,
      (openPositions) => openPositions?.[symbol],
      (a, b) => {
        if (a === b) return true;
        if (a?.length !== b?.length) return false;
        return a.every((itemA, i) => {
          const itemB = b[i];
          return (
            itemA.positionId === itemB.positionId &&
            itemA.slot === itemB.slot &&
            itemA.quantity === itemB.quantity &&
            itemA.takeProfit === itemB.takeProfit &&
            itemA.stopLoss === itemB.stopLoss
          );
        });
      },
    ),
  );

  const symbolTpSlOrdersAtomFamily = atomFamily((symbol: string) =>
    selectAtom(
      atom((get) => {
        const positions = get(openPositionsAtom)[symbol];
        if (!positions?.length) return [];
        const orders = get(v2OrdersAtom)?.[symbol] ?? [];
        const positionSlots = new Set(positions.map((p) => p.slot));
        return orders.filter(
          (o) =>
            (o.conditionalOrderType === ConditionalOrderTypeEnum.PositionTp ||
              o.conditionalOrderType === ConditionalOrderTypeEnum.PositionSl) &&
            positionSlots.has(o.slot),
        );
      }),
      (v) => v,
      (a, b) => {
        if (a === b) return true;
        if (a.length !== b.length) return false;
        return a.every((orderA, i) => {
          const orderB = b[i];
          return (
            orderA.orderId === orderB.orderId &&
            orderA.triggerPrice === orderB.triggerPrice &&
            orderA.qty === orderB.qty &&
            orderA.conditionalOrderType === orderB.conditionalOrderType
          );
        });
      },
    ),
  );

  return { symbolPositionAtomFamily, symbolTpSlOrdersAtomFamily };
});

interface TpSlPluginProps {
  /**
   * Label + anchor variant. Web full-chart uses `'full'` (long line anchored to
   * the left edge, label reads `"{TP,SL} Trigger {price} / {Market,Limit}"`).
   * Webview uses `'short'` (label pinned to the right edge next to the price
   * scale, reads `"{TP,SL} {Market,Limit}"`).
   */
  variant?: TpSlLabelVariant;
}

export const TpSlPlugin = memo(({ variant = 'full' }: TpSlPluginProps) => {
  const tv = useTvHeaderContext();
  const lineHelperRef = useRef<TpSlLineHelper>(null);
  const [symbol, setSymbol] = useState<string>('');
  const [checked, setChecked] = useTradingViewDataStorage({ key: 'TPSLVisible' });
  const { symbolPositionAtomFamily, symbolTpSlOrdersAtomFamily } = useBunja(tpSlBunja);

  const { symbol: symbolWithoutPriceType } = symbolNameToInfo(symbol);
  const symbolPositions = useAtomValue(symbolPositionAtomFamily(symbolWithoutPriceType));
  const tpSlOrders = useAtomValue(symbolTpSlOrdersAtomFamily(symbolWithoutPriceType));

  useEffect(() => {
    if (!lineHelperRef.current) lineHelperRef.current = new TpSlLineHelper(tv, variant);
    const handler: TvEventHandler<'onFirstDataLoaded'> = ({ symbol: newSymbol, symbolChanged }) => {
      if (!symbolChanged) return;
      lineHelperRef.current?.clean();
      setSymbol(newSymbol);
    };
    return tv.on('onFirstDataLoaded', handler);
  }, [tv, variant]);

  useEffect(() => {
    if (!checked.value || !symbolPositions || symbolPositions.length === 0) {
      lineHelperRef.current?.clean();
      return;
    }

    lineHelperRef.current?.draw(symbol, tpSlOrders, symbolPositions);
  }, [checked.value, symbol, symbolPositions, tpSlOrders]);

  useEffect(() => {
    return () => {
      lineHelperRef.current?.clean();
    };
  }, []);

  return (
    <Checkbox
      checked={checked.value}
      onChange={(e) => {
        setChecked(e.currentTarget.checked);
        segment.track(ChartEvent.ChartEventToggleClick, {
          event_name: 'TP / SL',
          action: e.currentTarget.checked ? 'turn_on' : 'turn_off',
        });
      }}
    >
      <FormattedMessage id='Trade.r250401.Tp_sl_label' />
    </Checkbox>
  );
});

type TpSlTypes = 'TP' | 'SL';

const TpSlLineMapper = {
  TP: {
    bodyColor: ChartStyles.Color.TriggerLong,
    lineColor: ChartStyles.Color.Up,
  },
  SL: {
    bodyColor: ChartStyles.Color.TriggerShort,
    lineColor: ChartStyles.Color.Down,
  },
};

class TpSlLineHelper {
  #lines: Record<string, IPositionLineAdapter> = {};

  constructor(
    private readonly tv: TradingView,
    private readonly variant: TpSlLabelVariant,
  ) {}

  draw(symbol: string, orders: Order[], positions: OpenPosition[]) {
    if (this.tv.symbol !== symbol) return;
    const activeOrderIds = new Set(orders.map((o) => o.orderId));
    Object.keys(this.#lines).forEach((id) => {
      if (!activeOrderIds.has(id)) this.#remove(id);
    });
    positions.forEach((position) => {
      position.tpOrders.forEach((order) => {
        const isEntire = Num(order?.qty || 0).isZero();
        const qty = isEntire ? Num(position?.quantity || 0).abs() : Num(order?.qty || 0).abs();
        return this.#draw(order, numberFormatter(qty));
      });
      position.slOrders.forEach((order) => {
        const isEntire = Num(order?.qty || 0).isZero();
        const qty = isEntire ? Num(position?.quantity || 0).abs() : Num(order?.qty || 0).abs();
        return this.#draw(order, numberFormatter(qty));
      });
    });
  }

  #draw(order: Order, qty: string) {
    try {
      if (!this.tv.enabled || !this.tv.activeChart) return;
      const type: TpSlTypes = order.conditionalOrderType === ConditionalOrderTypeEnum.PositionTp ? 'TP' : 'SL';
      const lineInfo = TpSlLineMapper[type];
      const price = Number(order.triggerPrice || 0);
      if (price === 0) {
        this.#remove(order.orderId);
        return;
      }
      if (!this.#lines[order.orderId]) {
        // `short` variant pins the label body to the right edge of the chart
        // (next to the price scale) by collapsing the line length to 0%.
        // `full` variant keeps `createPositionLine`'s default full-length line
        // anchored to the left, showing the long "Trigger / Market|Limit" label.
        let line = this.tv
          .createPositionLine()
          .setLineColor(lineInfo.lineColor)
          .setBodyTextColor(ChartStyles.Color.White)
          .setBodyBackgroundColor(lineInfo.bodyColor)
          .setBodyBorderColor(lineInfo.bodyColor)
          .setQuantityTextColor(ChartStyles.Color.White)
          .setQuantityBackgroundColor(ChartStyles.Color.Background)
          .setQuantityBorderColor(lineInfo.bodyColor);
        if (this.variant === 'short') line = line.setLineLength(0, 'percentage');
        this.#lines[order.orderId] = line;
      }
      const executionType = getTpSlExecutionLabel(order.orderType);
      const label = buildTpSlLabel({
        type,
        triggerPrice: this.variant === 'full' ? numberFormatter(String(order.triggerPrice ?? 0)) : '',
        executionType,
        variant: this.variant,
      });
      this.#lines[order.orderId].setText(label).setQuantity(qty).setPrice(price);
    } catch {
      // Value is null error
    }
  }

  #remove(id: string) {
    this.#lines[id]?.remove();
    delete this.#lines[id];
  }

  #cleanLines() {
    Object.values(this.#lines).forEach((line) => line?.remove());
    this.#lines = {};
  }

  clean() {
    this.#cleanLines();
  }
}
